/**
 * Auto-Reply API hooks — режим работы, readiness, undo, chat override.
 *
 * Phase 9: Auto-Reply Platform.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { CheckpointType } from '../../../shared/proposal-contract.js';
// Канал диалога — существующим объявлением, а не третьей копией союза
// 'email' | 'telegram' | 'widget': их в проекте и так больше десятка
import type { InboxItem } from './inbox';
import { client } from './client';

// ============================================================================
// Types
// ============================================================================

export type AutoReplyMode = 'shadow' | 'bot' | 'assistant';

/** Ось генерации: браться ли за черновик. Отдельно от оси доставки. */
export type GenerationMode = 'always' | 'known' | 'manual';

/** Откуда взялось действующее значение: чем ниже, тем ближе к диалогу. */
export type ModeSource = 'org' | 'channel' | 'chat';

export interface AutoReplySettings {
  mode: AutoReplyMode;
  generationMode: GenerationMode;
  /**
   * Правило канала — только СВОЁ; `null` означает «как в организации».
   *
   * Действующее значение здесь показывать нельзя: сразу после переключения
   * организации у всех трёх каналов появилось бы «своё» правило, которого нет.
   */
  channelGeneration: Record<InboxItem['channel'], GenerationMode | null>;
}

/**
 * Прогноз: под какое правило сколько диалогов попадает СЕЙЧАС.
 *
 * Без него включение «только в начатых беседах» неотличимо от поломки —
 * черновики просто перестают появляться, и понять, так задумано или сломалось,
 * не по чему.
 */
export interface GenerationForecast {
  /** Диалогов, где клиент писал хотя бы раз. */
  total: number;
  /** Из них тех, где мы уже отвечали, — то есть под правило `known`. */
  withOurReply: number;
  byChannel: Record<InboxItem['channel'], { total: number; withOurReply: number }>;
}

/** Что ДЕЙСТВУЕТ у диалога и откуда взялось — у каждой оси свой источник. */
export interface DialogueModes {
  chatId: string;
  mode: AutoReplyMode;
  modeSource: ModeSource;
  generationMode: GenerationMode;
  generationSource: ModeSource;
}

export interface ReadinessMetrics {
  readiness: number;
  correctionRate: number;
  escalationRate: number;
  totalProposals: number;
  autoSendEligible: number;
  assistantThreshold: number;
  assistantAvailable: boolean;
}

export interface CheckpointEvent {
  id: string;
  threadId: string;
  orgId: string;
  type: CheckpointType;
  createdBy: string;
  createdAt: string;
  metadata: Record<string, unknown> | null;
}

// ============================================================================
// Auto-Reply Settings
// ============================================================================

export function useAutoReplySettings() {
  return useQuery({
    queryKey: ['auto-reply-settings'],
    queryFn: async () => {
      const { data } = await client.get<AutoReplySettings>('/settings/auto-reply');
      return data;
    },
  });
}

export function useSetAutoReplyMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mode: AutoReplyMode) => {
      const { data } = await client.put<{ mode: AutoReplyMode }>('/settings/auto-reply', { mode });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auto-reply-settings'] }),
  });
}

export function useSetGenerationMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mode: GenerationMode) => {
      const { data } = await client.put<{ generationMode: GenerationMode }>(
        '/settings/generation',
        { mode },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auto-reply-settings'] }),
  });
}

/**
 * Правило генерации на канал — «в виджете готовить всегда, в почте по-старому».
 *
 * Уровень канала лежал в базе и читался движком с самого начала; 11.09
 * добавлен путь записи. `null` снимает правило.
 */
export function useSetChannelGenerationMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      channel,
      mode,
    }: {
      channel: InboxItem['channel'];
      mode: GenerationMode | null;
    }) => {
      const { data } = await client.put<{ channel: string; generationMode: GenerationMode | null }>(
        `/settings/generation/channels/${channel}`,
        { mode },
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['auto-reply-settings'] });
      // Шапка диалога показывает ДЕЙСТВУЮЩИЙ режим: он мог измениться вместе
      // с правилом канала, а до перезагрузки показывал бы прежний
      qc.invalidateQueries({ queryKey: ['dialogue-modes'] });
    },
  });
}

export function useGenerationForecast() {
  return useQuery({
    queryKey: ['generation-forecast'],
    queryFn: async () => {
      const { data } = await client.get<GenerationForecast>('/settings/generation/forecast');
      return data;
    },
    // Прогноз доступен администратору: считается по всем каналам организации,
    // а оператор видит только свои ящики. Повторять запрос при 403 незачем
    retry: false,
  });
}

export function useReadinessMetrics() {
  return useQuery({
    queryKey: ['auto-reply-readiness'],
    queryFn: async () => {
      const { data } = await client.get<ReadinessMetrics>('/settings/auto-reply/readiness');
      return data;
    },
    refetchInterval: 60_000, // Обновлять раз в минуту
  });
}

// ============================================================================
// Chat Override
// ============================================================================

/**
 * Какие режимы ДЕЙСТВУЮТ у этого диалога и откуда взялись.
 *
 * Заведено 10.09: до этого читать режим диалога было нечем, поэтому кнопка
 * «Забрать» рисовалась по локальной памяти вкладки (после перезагрузки —
 * заново «Забрать» у забранного чата), а бейдж режима не получал значения ни
 * от одного из трёх вызовов, то есть был мёртв.
 *
 * Канал обязателен: без него пропускается канальный уровень наследования, и
 * ответ «что действует» получается неверным.
 */
export function useDialogueModes(
  channel: InboxItem['channel'],
  chatId: string,
  enabled = true,
) {
  return useQuery({
    queryKey: ['dialogue-modes', channel, chatId],
    queryFn: async () => {
      const { data } = await client.get<DialogueModes>(`/chats/${chatId}/auto-reply`, {
        params: { channel },
      });
      return data;
    },
    enabled: enabled && !!chatId,
  });
}

/**
 * Переопределить режим диалога — одну ось или обе.
 *
 * `null` у оси означает «снять переопределение, наследовать снова»,
 * отсутствие поля — «эту ось не трогать». Различие приезжает с бэкенда: на
 * диалог строка ровно одна, и запись одной оси без него затирала бы вторую.
 */
export function useSetChatMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      chatId,
      mode,
      generationMode,
    }: {
      chatId: string;
      mode?: AutoReplyMode | null;
      generationMode?: GenerationMode | null;
    }) => {
      const { data } = await client.put<DialogueModes>(`/chats/${chatId}/auto-reply`, {
        ...(mode !== undefined ? { mode } : {}),
        ...(generationMode !== undefined ? { generationMode } : {}),
      });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbox'] });
      // Иначе шапка диалога показывала бы прежний режим до перезагрузки
      qc.invalidateQueries({ queryKey: ['dialogue-modes'] });
    },
  });
}

// ============================================================================
// Undo
// ============================================================================

export function useUndoAutoSend() {
  return useMutation({
    mutationFn: async (proposalId: string) => {
      const { data } = await client.post<{ success: boolean }>(`/auto-reply/${proposalId}/undo`);
      return data;
    },
  });
}

// ============================================================================
// Checkpoints
// ============================================================================

export function useCheckpointEvents(threadId: string | null) {
  return useQuery({
    queryKey: ['checkpoints', threadId],
    queryFn: async () => {
      if (!threadId) return [];
      const { data } = await client.get<{ events: CheckpointEvent[] }>(`/threads/${threadId}/checkpoints`);
      return data.events;
    },
    enabled: !!threadId,
  });
}

export function useCreateCheckpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ threadId, type }: { threadId: string; type: CheckpointEvent['type'] }) => {
      const { data } = await client.post<{ event: CheckpointEvent }>(`/threads/${threadId}/checkpoints`, { type });
      return data.event;
    },
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: ['checkpoints', vars.threadId] }),
  });
}

// ============================================================================
// Knowledge Promote
// ============================================================================

export function usePromoteKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ title, content }: { title: string; content: string }) => {
      const { data } = await client.post('/knowledge/promote', { title, content });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['knowledge'] }),
  });
}
